import { z } from "zod"
import { TranslationKey } from "../i18n/translation-key"
import { Discount } from "./discount"
import { DiscountAppliesTo, DiscountExclusions } from "./discount-code"

export const MAX_PHYSICAL_TAGS = 100

export enum JobStatus {
  BOOKED = "BOOKED", // Bokad
  CHECKED_IN = "CHECKED_IN", // Incheckad
  COLLECTED = "COLLECTED", // Inlämnad
  INSPECTION_DRAFT_SAVED = "INSPECTION_DRAFT_SAVED", // Besiktningsutkast sparat
  SERVICE_PROPOSAL_SENT = "SERVICE_PROPOSAL_SENT", // Serviceförslag skickat
  SERVICE_PROPOSAL_ACCEPTED = "SERVICE_PROPOSAL_ACCEPTED", // Redo för service
  COMPLETED = "COMPLETED", // Färdig
  DELIVERED = "DELIVERED", // Utlämnad
  CANCELLED = "CANCELLED", // Avbokad
}

export enum JobOrigin {
  JULO_BOOKING_BY_CUSTOMER = "JULO_BOOKING_BY_CUSTOMER",
  STORE_SITE_BOOKING_BY_CUSTOMER = "STORE_SITE_BOOKING_BY_CUSTOMER",
  EVENT_BOOKING_BY_CUSTOMER = "EVENT_BOOKING_BY_CUSTOMER",
  DROP_IN_BY_MECHANIC = "DROP_IN_BY_MECHANIC",
  BOOKING_BY_MECHANIC = "BOOKING_BY_MECHANIC",
  NEW_MANUAL_RECEPTION = "NEW_MANUAL_RECEPTION",
  DROP_IN_BY_CUSTOMER_CHECK_IN = "DROP_IN_BY_CUSTOMER_CHECK_IN",
}

export const SetJobStatusSchema = z.object({
  newStatus: z.nativeEnum(JobStatus),
  // Moving to Färdig must not send the automatic "cykeln är klar" SMS: the
  // store chose not to text, or the caller just sent its own message.
  skipReadySms: z.boolean().optional(),
})

/** No automatic pickup reminders up to and including this day; null resumes them. */
export const SetPickupReminderPauseSchema = z.object({
  pausedUntil: z.coerce.date<Date>().nullable(),
})

export const SetIsWaitingForPartsSchema = z.object({
  isWaitingForParts: z.boolean(),
})

export const SetWaitingForPartsWithDeliveryDateSchema = z.object({
  expectedDeliveryDate: z.coerce.date<Date>(),
  message: z
    .string()
    .min(1, "common.validation.messageRequired" satisfies TranslationKey),
  sendSms: z.boolean(),
})

export const ClearWaitingForPartsSchema = z.object({
  message: z
    .string()
    .min(1, "common.validation.messageRequired" satisfies TranslationKey),
  sendSms: z.boolean(),
})

export const SetIsTemporarilyHandedOutSchema = z.object({
  isTemporarilyHandedOut: z.boolean(),
})

export const SetSendReviewRequestSchema = z.object({
  sendReviewRequest: z.boolean(),
})

export const SetInvoicedSchema = z.object({
  invoiced: z.boolean(),
})

export const UpdateLeasingIdSchema = z.object({
  leasingId: z.string().trim().max(200).nullable(),
})

export const UpdatePhysicalTagSchema = z.object({
  physicalTag: z.coerce
    .number<number>()
    .min(1, "common.validation.physicalTagRange" satisfies TranslationKey)
    .max(
      MAX_PHYSICAL_TAGS,
      "common.validation.physicalTagRange" satisfies TranslationKey,
    )
    .nullable(),
})

/** How many jobs a job counts as when the store's daily capacity is filled. */
export const JobSizeSchema = z.coerce
  .number<number>()
  .min(0.5, "common.validation.sizeBetweenHalfAndTen" satisfies TranslationKey)
  .max(10, "common.validation.sizeBetweenHalfAndTen" satisfies TranslationKey)
  .multipleOf(0.5, "common.validation.sizeStep" satisfies TranslationKey)

export const UpdateSizeSchema = z.object({
  size: JobSizeSchema,
})

export const UpdateAssignedMechanicSchema = z.object({
  assignedMechanicId: z.guid().nullable(),
})

export const UpdateReceptionMechanicSchema = z.object({
  receptionMechanicId: z.guid(),
})

export const UpdateInspectionMechanicSchema = z.object({
  inspectionMechanicId: z.guid(),
})

export type SetJobStatus = z.infer<typeof SetJobStatusSchema>
export type SetPickupReminderPause = z.infer<
  typeof SetPickupReminderPauseSchema
>
export type SetIsWaitingForParts = z.infer<typeof SetIsWaitingForPartsSchema>
export type SetWaitingForPartsWithDeliveryDate = z.infer<
  typeof SetWaitingForPartsWithDeliveryDateSchema
>
export type ClearWaitingForParts = z.infer<typeof ClearWaitingForPartsSchema>
export type SetIsTemporarilyHandedOut = z.infer<
  typeof SetIsTemporarilyHandedOutSchema
>
export type SetSendReviewRequest = z.infer<typeof SetSendReviewRequestSchema>
export type SetInvoiced = z.infer<typeof SetInvoicedSchema>
export type UpdatePhysicalTag = z.infer<typeof UpdatePhysicalTagSchema>
export type UpdateLeasingId = z.infer<typeof UpdateLeasingIdSchema>
export type UpdateSize = z.infer<typeof UpdateSizeSchema>
export type UpdateAssignedMechanic = z.infer<
  typeof UpdateAssignedMechanicSchema
>
export type UpdateReceptionMechanic = z.infer<
  typeof UpdateReceptionMechanicSchema
>
export type UpdateInspectionMechanic = z.infer<
  typeof UpdateInspectionMechanicSchema
>

export const UpdateHandInDateSchema = z.object({
  handInDate: z.coerce.date<Date>(),
})
export type UpdateHandInDate = z.infer<typeof UpdateHandInDateSchema>

export const UpdateJobPriceListSchema = z.object({
  priceListId: z.guid().nullable(),
})
export type UpdateJobPriceList = z.infer<typeof UpdateJobPriceListSchema>

export const BulkUpdateStatusSchema = z.object({
  jobIds: z.array(z.guid()).min(1),
  newStatus: z.nativeEnum(JobStatus),
  // See SetJobStatusSchema.
  skipReadySms: z.boolean().optional(),
})

export const BulkUpdateAssignedMechanicSchema = z.object({
  jobIds: z.array(z.guid()).min(1),
  assignedMechanicId: z.guid().nullable(),
})

export const BulkUpdateHandInDateSchema = z.object({
  jobIds: z.array(z.guid()).min(1),
  handInDate: z.coerce.date<Date>(),
})

export type BulkUpdateStatus = z.infer<typeof BulkUpdateStatusSchema>
export type BulkUpdateAssignedMechanic = z.infer<
  typeof BulkUpdateAssignedMechanicSchema
>
export type BulkUpdateHandInDate = z.infer<typeof BulkUpdateHandInDateSchema>

export interface DiscountCodeOnJob {
  id: string
  name: string
  discount: Discount
  appliesTo: DiscountAppliesTo
  servicePackageId: string | null
  servicePackageName: string | null
  /** The job's copy, taken when the code was applied. */
  exclusions: DiscountExclusions
}

export interface Job {
  id: string
  displayNumber: number
  handInDate: Date
  status: JobStatus
  origin: JobOrigin
  numUnreadMessageFromCustomer: number
  customerBudget: number
  physicalTag: number | null
  isTemporarilyHandedOut: boolean
  sendReviewRequest: boolean
  isBooking: boolean
  orderDiscount: Discount | null
  laborDiscount: Discount | null
  productDiscount: Discount | null
  discountCode: DiscountCodeOnJob | null
  size: number

  storeId: string
  serviceEventId: string | null
  priceListId: string | null
  companyContactPersonId: string | null
  reference: string | null
  eServiceNumber: string | null
  invoicedAt: Date | null
  createdAt: Date
  updatedAt: Date
}

export const MAX_FILES_PER_JOB = 10
export const MAX_PDF_SIZE_BYTES = 20 * 1_000_000
export const IMAGE_COMPRESSION_TARGET_BYTES = 1 * 1_000_000

export const ACCEPTED_JOB_ATTACHMENT_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
] as const

export type AcceptedJobAttachmentMimeType =
  (typeof ACCEPTED_JOB_ATTACHMENT_MIME_TYPES)[number]

export interface JobAttachment {
  id: string
  jobId: string
  url: string
  name: string
  size: number
  mimeType: string
  createdAt: Date
}

/**
 * Files uploaded to the staging endpoint before a job exists (e.g. during a
 * new manual reception). The submit payload carries these so the backend can
 * create JobAttachment rows pointing to the already-uploaded GCS objects.
 */
export const StagedJobAttachmentSchema = z.object({
  url: z.string().url().max(1000),
  name: z.string().min(1).max(500),
  size: z.number().int().positive(),
  mimeType: z.string().min(1).max(100),
})
export type StagedJobAttachment = z.infer<typeof StagedJobAttachmentSchema>
